import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalSize } from '@/shared/components/ui/Modal';
import ViewSettingsForm from './ViewSettingsForm';
import { ViewSettings } from './types';
import { usePreferences } from '@/shared/hooks/usePreferences';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { ErrorState } from '@/shared/ui/states/ErrorState';

interface ViewSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ViewSettingsModal: React.FC<ViewSettingsModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { data: preferences, isLoading, isFetching, isError, error, refetch, update, isUpdating } = usePreferences({ autoFetch: false });
  const hasFetchedOnOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !hasFetchedOnOpen.current) {
      refetch();
      hasFetchedOnOpen.current = true;
    } else if (!isOpen) {
      hasFetchedOnOpen.current = false;
    }
  }, [isOpen, refetch]);
  
  const handleSubmit = (values: ViewSettings) => {
    update(values, {
        onSuccess: () => {
            onClose();
        }
    });
  }

  const renderContent = () => {
    // Show loading state on initial fetch and on background refetches (e.g. after a successful update)
    // This ensures ViewSettingsForm always mounts with fresh data, preventing stale useState initialisation
    if (isLoading || isFetching) {
      return <LoadingState />;
    }

    if (isError) {
      return <ErrorState error={error ? error.message : 'An unknown error occurred'} />;
    }

    if (preferences) {
      return (
        <ViewSettingsForm
          initialValues={preferences}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isUpdating={isUpdating}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('viewSettings.title')}
      size={ModalSize.Xxxl}
      preventCloseOnEsc
    >
      {renderContent()}
    </Modal>
  );
};

export default ViewSettingsModal;